import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, setDoc, updateDoc, serverTimestamp, runTransaction, addDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, uploadString } from "firebase/storage";
import ClientModal from '../components/ClientModal';
import MaterialModal from '../components/MaterialModal';
import PromptManagerModal from '../components/PromptManagerModal';
import { NEW_JOB_TABS_CONFIG } from '../components/NewJobView/newJobViewTabsConfig';

const NewJobView = ({ user, db, storage, initialJobData }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const initialJobState = useCallback(() => ({
        clientId: '', jobName: '', salesOrder: '', purchaseOrder: '', description: '',
        deliveryDate: '', priority: 'Media', type: 'Producto', product: '', class: '',
        coverImage: { file: null, preview: '', url: '' }, quantity: 1,
        components: [{ name: '', code: '', qtyPerProduct: 1, materialId: '', image: { file: null, preview: '', url: '' }, pieces: [] }],
        hasBlocks: false, blocks: [{ name: '', code: '' }],
        assignedRoles: [],
        documents: [{ file: null, description: '', name: '', url: '' }],
        aiPrompt: '',
        status: { stage: 'NUEVO', phase: 'PENDIENTE' },
    }), []);

    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [materialsCatalog, setMaterialsCatalog] = useState([]);
    const [users, setUsers] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveFeedback, setSaveFeedback] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentJobId, setCurrentJobId] = useState(null);
    const [clientNameInput, setClientNameInput] = useState('');
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [jobData, setJobData] = useState(initialJobState);
    // Permite abrir directo en una pestaña vía ?tab=details (p. ej. al venir de "Agregar bloque/componente").
    const requestedTab = searchParams.get('tab');
    const [activeTabId, setActiveTabId] = useState(
        NEW_JOB_TABS_CONFIG.some(t => t.id === requestedTab) ? requestedTab : NEW_JOB_TABS_CONFIG[0].id
    );
    const [uploadProgress, setUploadProgress] = useState({});
    const [savedAiPrompts, setSavedAiPrompts] = useState([]);
    const [isPromptManagerOpen, setIsPromptManagerOpen] = useState(false);

    useEffect(() => {
        const initializeData = async () => {
            setIsLoading(true);
            try {
                const clientsSnapshot = await getDocs(collection(db, "clients"));
                setClients(clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.name.localeCompare(b.name)));
                
                const materialsSnapshot = await getDocs(collection(db, "materials"));
                setMaterialsCatalog(materialsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.name.localeCompare(b.name)));
                
                const settingsSnap = await getDoc(doc(db, "project_settings", "PROYECTO-X"));
                if (settingsSnap.exists()) setProducts((settingsSnap.data().products || []).sort());
                
                setUsers((await getDocs(collection(db, "users"))).docs.map(doc => ({ uid: doc.id, ...doc.data() })));
                setAllRoles((await getDocs(collection(db, "roles"))).docs.map(doc => doc.data().name));
                
                const promptsSnapshot = await getDocs(collection(db, "ai_prompts"));
                setSavedAiPrompts(promptsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b) => a.name.localeCompare(b.name)));
                
                const initialState = initialJobState();
                if (initialJobData) {
                    const mergedData = {
                        ...initialState, ...initialJobData,
                        coverImage: { file: null, preview: initialJobData.coverImageUrl || '', url: initialJobData.coverImageUrl || '' },
                        components: initialJobData.components?.length > 0 ? initialJobData.components.map(c => ({...c, image: { file: null, preview: c.imageUrl || '', url: c.imageUrl || '' }, pieces: c.pieces || [] })) : initialState.components,
                        documents: initialJobData.documents?.map(d => ({...d, file: null})) || initialState.documents,
                    };
                    setJobData(mergedData);
                    setCurrentJobId(initialJobData.id);
                } else {
                    setJobData(initialState);
                    setCurrentJobId(null);
                }
            } catch (error) {
                console.error("Error al inicializar:", error);
                setErrorMessage("No se pudo cargar la configuración inicial.");
            } finally {
                setIsLoading(false);
            }
        };
        initializeData();
    }, [db, initialJobData, initialJobState]);

    // --- INICIO DE LA CORRECCIÓN: Mecanismo de Autocorrección de Estado ---
    useEffect(() => {
        // Este efecto vigila si algún componente en el estado pierde su array 'pieces' y lo repara.
        if (jobData.components && jobData.components.some(c => !Array.isArray(c.pieces))) {
            setJobData(prevData => ({
                ...prevData,
                components: prevData.components.map(c => ({
                    ...c,
                    pieces: Array.isArray(c.pieces) ? c.pieces : [], // Si 'pieces' no es un array, lo reestablece a uno vacío.
                }))
            }));
        }
    }, [jobData.components]);
    // --- FIN DE LA CORRECCIÓN ---

    useEffect(() => {
        if (jobData.clientId && clients.length > 0) {
            const client = clients.find(c => c.id === jobData.clientId);
            if (client) setClientNameInput(client.name);
        } else if (!jobData.clientId) {
            setClientNameInput('');
        }
    }, [jobData.clientId, clients]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setJobData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleDynamicChange = (index, event, field) => {
        const { name, value } = event.target;
        setJobData(p => ({ ...p, [field]: p[field].map((item, i) => i === index ? { ...item, [name]: value } : item) }));
    };
    const handleFileChange = (index, event) => {
        const file = event.target.files[0];
        if (file) setJobData(p => ({ ...p, documents: p.documents.map((item, i) => i === index ? { ...item, file, name: file.name } : item) }));
    };
    const handleCoverImageChange = (file) => {
        if (file) setJobData(p => ({ ...p, coverImage: { file, preview: URL.createObjectURL(file), url: '' } }));
    };
    const handleComponentImageChange = (file, index) => {
        if (file) setJobData(p => ({ ...p, components: p.components.map((item, i) => i === index ? { ...item, image: { file, preview: URL.createObjectURL(file), url: '' } } : item) }));
    };
    const handlePaste = (e, callback) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                callback(items[i].getAsFile());
                e.preventDefault();
                break;
            }
        }
    };
    const addDynamicField = (field) => {
        const newItem = {
            components: { name: '', code: '', qtyPerProduct: 1, materialId: '', image: { file: null, preview: '', url: '' }, pieces: [] },
            blocks: { name: '', code: '' },
            documents: { file: null, description: '', name: '', url: '' },
        }[field];
        if (newItem) setJobData(p => ({ ...p, [field]: [...(p[field] || []), newItem] }));
    };
    const removeDynamicField = (index, field) => {
        setJobData(p => ({ ...p, [field]: p[field].filter((_, i) => i !== index) }));
    };
    const handleClientInputChange = (e) => {
        const name = e.target.value.toUpperCase();
        setClientNameInput(name);
        const client = clients.find(c => c.name.toUpperCase() === name);
        setJobData(p => ({ ...p, clientId: client ? client.id : '' }));
    };
    const handleBlockCodeBlur = (index) => {
        setJobData(p => ({ ...p, blocks: p.blocks.map((b, i) => (i === index && /^\d+$/.test(b.code)) ? { ...b, code: b.code.padStart(3, '0') } : b) }));
    };
    
    const handleSaveJob = async (e) => {
        e.preventDefault();

        // Regla: cada componente CON NOMBRE debe tener código (se usa para nombrar planchas y alimentar
        // inventario). Los componentes totalmente vacíos se ignoran.
        const componentsSinCodigo = (jobData.components || []).filter(c => (c?.name || '').trim() !== '' && (c?.code || '').trim() === '');
        if (componentsSinCodigo.length > 0) {
            setErrorMessage('Cada componente debe tener un código. Complétalo en la pestaña "Detalles del Trabajo".');
            setActiveTabId('details');
            return;
        }

        // Regla global del intake (S4a): imagen + cliente + nombre, los tres
        // obligatorios — igual que en NewJobLite y en el formulario de solicitud.
        if (!jobData.clientId) {
            setErrorMessage('El cliente es obligatorio. Elígelo (o créalo) en "Información General".');
            setActiveTabId(NEW_JOB_TABS_CONFIG[0].id);
            return;
        }
        if (!(jobData.jobName || '').trim()) {
            setErrorMessage('El nombre del trabajo es obligatorio.');
            setActiveTabId(NEW_JOB_TABS_CONFIG[0].id);
            return;
        }
        // El preview del trabajo es OBLIGATORIO (se muestra en el Dashboard).
        const hasCover = !!(jobData.coverImage?.file || jobData.coverImage?.url || jobData.coverImage?.preview);
        if (!hasCover) {
            setErrorMessage('El preview del trabajo es obligatorio. Sube o pega una imagen en "Información General".');
            setActiveTabId(NEW_JOB_TABS_CONFIG[0].id);
            return;
        }

        setIsSaving(true);

        try {
            let jobIdToSave = currentJobId;
            let jobNumber = jobData.jobNumber;

            if (!jobIdToSave) {
                const counterRef = doc(db, "counters", "jobs");
                jobNumber = await runTransaction(db, async (transaction) => {
                    const counterDoc = await transaction.get(counterRef);
                    if (!counterDoc.exists()) throw new Error("¡El contador de trabajos no existe!");
                    const newCount = (counterDoc.data().lastNumber || 0) + 1;
                    transaction.update(counterRef, { lastNumber: newCount });
                    return newCount;
                });
                jobIdToSave = `J-${jobNumber}`;
            }
            
            const uploadFile = (file, path) => {
                return new Promise((resolve, reject) => {
                    if(!file) return resolve('');
                    const storageRef = ref(storage, path);
                    const uploadTask = uploadBytesResumable(storageRef, file);
                    uploadTask.on('state_changed', 
                      (snapshot) => {}, 
                      (error) => reject(error), 
                      async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve(downloadURL);
                      }
                    );
                });
            };
            
            // Sube una imagen que viene como dataURL base64 (pegada/seleccionada con el uploader
            // de preview). Devuelve la URL de descarga, o '' si no es un dataURL.
            const uploadDataUrl = async (dataUrl, path) => {
                if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image')) return '';
                const storageRef = ref(storage, path);
                await uploadString(storageRef, dataUrl, 'data_url');
                return getDownloadURL(storageRef);
            };

            const coverImageUrl = jobData.coverImage.file ? await uploadFile(jobData.coverImage.file, `jobs/${jobIdToSave}/coverImage`) : jobData.coverImage.url;

            const componentsWithUrls = await Promise.all(
                (jobData.components || []).map(async (comp, index) => {
                    // FIX: el uploader de preview guarda la imagen en `image.preview` (base64),
                    // NO en `image.file`. Antes solo se subía `image.file` → la imagen del
                    // componente se perdía. Ahora: file → preview(base64) → url existente.
                    let imageUrl = '';
                    if (comp.image?.file) {
                        imageUrl = await uploadFile(comp.image.file, `jobs/${jobIdToSave}/component_${index}`);
                    } else if (comp.image?.preview?.startsWith?.('data:image')) {
                        imageUrl = await uploadDataUrl(comp.image.preview, `jobs/${jobIdToSave}/component_${index}`);
                    } else {
                        imageUrl = comp.image?.url || comp.imageUrl || '';
                    }
                    return { ...comp, imageUrl, image: null };
                })
            );
            
            const documentsWithUrls = await Promise.all(
                (jobData.documents || []).map(async (docItem, index) => {
                    const url = docItem.file ? await uploadFile(docItem.file, `jobs/${jobIdToSave}/docs/${docItem.file.name}`) : docItem.url;
                    return { ...docItem, url, file: null };
                })
            );

            const dataToSave = { 
                ...jobData, 
                jobNumber,
                coverImageUrl,
                components: componentsWithUrls,
                documents: documentsWithUrls
            };
            delete dataToSave.coverImage;

            const jobRef = doc(db, "jobs", jobIdToSave);
            if (currentJobId) {
                await updateDoc(jobRef, { ...dataToSave, lastUpdatedAt: serverTimestamp(), lastUpdatedBy: user.uid });
            } else {
                await setDoc(jobRef, { ...dataToSave, createdAt: serverTimestamp(), createdBy: user.uid });
            }

            setSaveFeedback('saved');
            setTimeout(() => {
                setSaveFeedback(null);
                navigate(`/jobs/${jobIdToSave}`);
            }, 1500);

        } catch (error) {
            console.error("Error al guardar:", error);
            setSaveFeedback('error');
            setErrorMessage(`Error: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const assignedMembers = users.filter(u => u.roles && u.roles.some(role => jobData.assignedRoles?.includes(role)));

    const renderActiveTabContent = () => {
        const activeTab = NEW_JOB_TABS_CONFIG.find(tab => tab.id === activeTabId);
        if (!activeTab) return null;
        const ActiveTabComponent = activeTab.component;
        const tabProps = {
            jobData, setJobData, handleInputChange, handleDynamicChange, addDynamicField, removeDynamicField,
            clients, clientNameInput, handleClientInputChange, setIsClientModalOpen,
            products, materialsCatalog, setIsMaterialModalOpen, allRoles, 
            assignedMembers, handleFileChange, handleCoverImageChange,
            handleComponentImageChange, handlePaste, handleBlockCodeBlur,
            uploadProgress, savedAiPrompts,
            setIsPromptManagerOpen,
            users
        };
        return <ActiveTabComponent {...tabProps} />;
    };

    if (isLoading) {
        return <div className="p-8 text-center">Cargando...</div>;
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
            <ClientModal isOpen={isClientModalOpen} onClose={() => setIsClientModalOpen(false)} onSave={() => {}} />
            <MaterialModal isOpen={isMaterialModalOpen} onClose={() => setIsMaterialModalOpen(false)} onSave={() => {}} />
            <PromptManagerModal
                isOpen={isPromptManagerOpen}
                onClose={() => setIsPromptManagerOpen(false)}
                prompts={savedAiPrompts}
                onUpdatePrompt={async () => {}}
                onDeletePrompt={async () => {}}
                onSaveNewPrompt={async () => {}}
            />
            
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">
                    {currentJobId ? `Editando Job #${jobData.jobNumber}` : 'Crear Nuevo Trabajo'}
                </h1>
            </div>

            {errorMessage && <div className="bg-red-100 border-red-400 text-red-700 px-4 py-3 rounded mb-4">{errorMessage}</div>}

            <form onSubmit={handleSaveJob} className="space-y-8">
                <fieldset disabled={isSaving} className="space-y-8">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            {NEW_JOB_TABS_CONFIG.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTabId === activeTabId;
                                return (
                                    <button
                                        type="button"
                                        key={tab.id}
                                        onClick={() => setActiveTabId(tab.id)}
                                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'}`}
                                    >
                                        <Icon size={16} /> {tab.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                    <div className="mt-4 bg-white p-6 rounded-lg shadow-md">
                        {renderActiveTabContent()}
                    </div>
                </fieldset>
                
                <div className="flex justify-end items-center pt-6 border-t border-gray-200 gap-4">
                    <button type="button" onClick={() => navigate(initialJobData ? `/jobs/${initialJobData.id}` : '/dashboard')} disabled={isSaving} className="px-8 py-3 font-bold text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                        Cancelar
                    </button>
                    <button type="submit" disabled={isSaving} className={`px-8 py-3 font-bold text-white rounded-lg transition-colors w-48 ${saveFeedback === 'saved' ? 'bg-green-500' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        {isSaving ? 'Guardando...' : (saveFeedback === 'saved' ? '¡Guardado!' : (currentJobId ? 'Guardar Cambios' : 'Crear Trabajo'))}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default NewJobView;

